/**
 * Custom Orders Page - Commission request form
 */

import { ContentBlock, Box } from 'braid-design-system';
import type React from 'react';
import { useState, type ChangeEvent, type FormEvent } from 'react';

import {
  MAX_EMAIL_LENGTH,
  MAX_MESSAGE_LENGTH,
  MAX_NAME_LENGTH,
  MAX_SUBJECT_LENGTH,
  MIN_MESSAGE_LENGTH,
} from '../../../../shared';
import { PageHead } from '../../components/PageHead/PageHead';
import { API_ENDPOINT } from '../../constants/api';

import * as styles from './CustomOrders.css';

interface FormData {
  name: string;
  email: string;
  details: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  details?: string;
}

export const CustomOrders: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    details: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<
    'idle' | 'success' | 'error'
  >('idle');
  const [submitMessage, setSubmitMessage] = useState('');

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    if (!formData.email.trim() || !emailRegex.test(formData.email)) {
      newErrors.email = 'Valid email is required';
    }
    if (!formData.details.trim()) {
      newErrors.details = 'Please describe your request';
    } else if (formData.details.trim().length < MIN_MESSAGE_LENGTH) {
      newErrors.details = `Details must be at least ${MIN_MESSAGE_LENGTH} characters`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name.slice(0, MAX_NAME_LENGTH),
          email: formData.email.slice(0, MAX_EMAIL_LENGTH),
          subject: 'Custom order request'.slice(0, MAX_SUBJECT_LENGTH),
          message: formData.details.slice(0, MAX_MESSAGE_LENGTH),
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSubmitStatus('success');
        setSubmitMessage(
          data.message || 'Thank you! We will be in touch soon.',
        );
        setFormData({
          name: '',
          email: '',
          details: '',
        });
      } else {
        setSubmitStatus('error');
        setSubmitMessage(
          data.error || 'Something went wrong. Please try again.',
        );
      }
    } catch {
      setSubmitStatus('error');
      setSubmitMessage(
        'Unable to send your request. Please email info@faeforest.com.au',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ContentBlock width="large">
      <PageHead
        title="Custom orders"
        description="Commission a custom fairy door, garden creature, or accessory from Fae Forest."
        path="/custom-orders"
      />
      <Box paddingY="large">
        <div className={styles.container}>
          <h1 className={styles.heading}>Custom Orders & Commissions</h1>
          <p className={styles.description}>
            Want something unique for your fairy garden? Tell us about your
            vision and we will work with you on a custom painted piece or
            bespoke design.
          </p>

          {submitStatus === 'success' && (
            <div className={styles.successMessage}>{submitMessage}</div>
          )}
          {submitStatus === 'error' && (
            <div className={styles.errorMessage}>{submitMessage}</div>
          )}

          {submitStatus !== 'success' && (
            <form onSubmit={handleSubmit}>
              <div className={styles.formGroup}>
                <label htmlFor="name" className={styles.label}>
                  Name *
                </label>
                <input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={styles.input}
                  disabled={isSubmitting}
                />
                {errors.name && (
                  <div className={styles.error}>{errors.name}</div>
                )}
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="email" className={styles.label}>
                  Email *
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={styles.input}
                  disabled={isSubmitting}
                />
                {errors.email && (
                  <div className={styles.error}>{errors.email}</div>
                )}
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="details" className={styles.label}>
                  Details *
                </label>
                <p id="details-hint" className={styles.hint}>
                  Include anything that would help us quote your piece, such as
                  piece type, finish, approximate size, budget range, and
                  timeline.
                </p>
                <textarea
                  id="details"
                  name="details"
                  value={formData.details}
                  onChange={handleChange}
                  className={styles.textarea}
                  aria-describedby="details-hint"
                  disabled={isSubmitting}
                />
                {errors.details && (
                  <div className={styles.error}>{errors.details}</div>
                )}
              </div>

              <button
                type="submit"
                className={styles.button}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Sending...' : 'Submit request'}
              </button>
            </form>
          )}
        </div>
      </Box>
    </ContentBlock>
  );
};
